/*
  SALC Motion module
  Lightweight carousel controller for Shopify sections.

  Required markup:
  - [data-salc-motion] root
  - [data-salc-motion-viewport] viewport
  - [data-salc-motion-track] track
  - [data-salc-motion-item] slide items

  Optional controls:
  - [data-salc-motion-prev]
  - [data-salc-motion-next]
  - [data-salc-motion-dots]
*/
(function () {
  'use strict';

  if (window.SALCMotion && window.initSALCMotion) {
    window.initSALCMotion(document);
    return;
  }

  var ROOT_SELECTOR = '[data-salc-motion]';
  var INSTANCE_KEY = 'salcMotion';
  var DEFAULTS = {
    loop: false,
    autoplay: false,
    autoplayDelay: 4000,
    speed: 320,
    draggable: true,
    pageDots: true,
    prevNextButtons: true,
    pauseOnHover: true,
    watchCSS: false,
    centerMode: false,
    centerMobile: false
  };

  function toBoolean(value, fallback) {
    if (value === undefined || value === null || value === '') return fallback;
    if (value === true || value === 'true') return true;
    if (value === false || value === 'false') return false;
    return fallback;
  }

  function toNumber(value, fallback) {
    var number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function readOption(root, names, fallback, parser) {
    for (var index = 0; index < names.length; index += 1) {
      if (root.dataset[names[index]] !== undefined) {
        return parser(root.dataset[names[index]], fallback);
      }
    }

    return fallback;
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(value, max));
  }

  function normalizeIndex(index, count) {
    return ((index % count) + count) % count;
  }

  function debounce(callback, delay) {
    var timeout;

    return function () {
      var args = arguments;
      window.clearTimeout(timeout);
      timeout = window.setTimeout(function () {
        callback.apply(null, args);
      }, delay);
    };
  }

  function getWatchCSSValue(root) {
    var content = window.getComputedStyle(root, ':after').content;
    return content ? content.replace(/["']/g, '').trim() : '';
  }

  function releasePointerCaptureSafely(target, pointerId) {
    if (!target || pointerId === null || pointerId === undefined) return;

    try {
      target.releasePointerCapture(pointerId);
    } catch (error) {
      return;
    }
  }

  function SALCMotion(root) {
    if (!root || root[INSTANCE_KEY]) return root ? root[INSTANCE_KEY] : null;

    this.root = root;
    this.viewport = root.querySelector('[data-salc-motion-viewport]');
    this.track = root.querySelector('[data-salc-motion-track]');
    this.prevButton = root.querySelector('[data-salc-motion-prev]');
    this.nextButton = root.querySelector('[data-salc-motion-next]');
    this.dotsContainer = root.querySelector('[data-salc-motion-dots]');
    this.options = this.readOptions();
    this.items = [];
    this.realItems = [];
    this.dots = [];
    this.index = 0;
    this.positionIndex = 0;
    this.cloneCount = 0;
    this.positions = [];
    this.slideStops = [];
    this.timer = null;
    this.loopTimeout = null;
    this.pointerId = null;
    this.startX = 0;
    this.startY = 0;
    this.dragDelta = 0;
    this.dragAxisLocked = false;
    this.dragMoved = false;
    this.suppressClickUntil = 0;
    this.isPointerDown = false;
    this.isDestroyed = false;
    this.isEnabled = true;
    this.eventsBound = false;
    this.unbinders = [];
    this.resizeObserver = null;

    root[INSTANCE_KEY] = this;
    this.init();
  }

  SALCMotion.prototype.readOptions = function () {
    return {
      loop: readOption(this.root, ['salcMotionLoop', 'loop'], DEFAULTS.loop, toBoolean),
      autoplay: readOption(this.root, ['salcMotionAutoplay', 'autoplay'], DEFAULTS.autoplay, toBoolean),
      autoplayDelay: readOption(this.root, ['salcMotionAutoplayDelay', 'autoplayDelay'], DEFAULTS.autoplayDelay, toNumber),
      speed: readOption(this.root, ['salcMotionSpeed', 'animationDuration'], DEFAULTS.speed, toNumber),
      draggable: readOption(this.root, ['salcMotionDraggable', 'draggable'], DEFAULTS.draggable, toBoolean),
      pageDots: readOption(this.root, ['salcMotionPageDots', 'pageDots'], DEFAULTS.pageDots, toBoolean),
      prevNextButtons: readOption(this.root, ['salcMotionPrevNextButtons', 'prevNextButtons'], DEFAULTS.prevNextButtons, toBoolean),
      pauseOnHover: readOption(this.root, ['salcMotionPauseOnHover', 'pauseOnHover'], DEFAULTS.pauseOnHover, toBoolean),
      watchCSS: readOption(this.root, ['salcMotionWatchCss', 'watchCss', 'watchCSS'], DEFAULTS.watchCSS, toBoolean),
      centerMode: readOption(this.root, ['salcMotionCenterMode', 'centerMode'], DEFAULTS.centerMode, toBoolean),
      centerMobile: readOption(this.root, ['salcMotionCenterMobile', 'centerMobile'], DEFAULTS.centerMobile, toBoolean)
    };
  };

  SALCMotion.prototype.init = function () {
    if (!this.viewport || !this.track) {
      this.destroy();
      return;
    }

    if (this.options.watchCSS && !this.shouldEnableByCSS()) {
      this.disable();
      this.bindResize();
      return;
    }

    this.realItems = this.getRealItems();

    if (!this.realItems.length) {
      this.destroy();
      return;
    }

    this.root.classList.add('is-salc-motion-ready');
    this.setupClones();
    this.items = Array.prototype.slice.call(this.track.children);
    this.positionIndex = this.options.loop ? this.cloneCount : 0;
    this.bindEvents();
    this.bindResize();
    this.recalculate(false);
    this.startAutoplay();
  };

  SALCMotion.prototype.shouldEnableByCSS = function () {
    var value = getWatchCSSValue(this.root);
    return value === 'salc-motion' || value === 'flickity';
  };

  SALCMotion.prototype.getRealItems = function () {
    return Array.prototype.slice.call(
      this.track.querySelectorAll('[data-salc-motion-item]:not([data-salc-motion-clone])')
    );
  };

  SALCMotion.prototype.addListener = function (target, eventName, callback, options) {
    if (!target) return;

    target.addEventListener(eventName, callback, options || false);
    this.unbinders.push(function () {
      target.removeEventListener(eventName, callback, options || false);
    });
  };

  SALCMotion.prototype.bindEvents = function () {
    if (this.eventsBound) return;

    this.eventsBound = true;
    var self = this;

    if (this.options.prevNextButtons) {
      this.addListener(this.prevButton, 'click', function () {
        self.previous();
      });
      this.addListener(this.nextButton, 'click', function () {
        self.next();
      });
    }

    if (this.options.pauseOnHover) {
      this.addListener(this.root, 'mouseenter', function () {
        self.stopAutoplay();
      });
      this.addListener(this.root, 'mouseleave', function () {
        self.startAutoplay();
      });
      this.addListener(this.root, 'focusin', function () {
        self.stopAutoplay();
      });
      this.addListener(this.root, 'focusout', function () {
        self.startAutoplay();
      });
    }

    if (this.options.draggable) {
      this.addListener(this.viewport, 'pointerdown', function (event) {
        self.onPointerDown(event);
      });
      this.addListener(this.viewport, 'pointermove', function (event) {
        self.onPointerMove(event);
      });
      this.addListener(this.viewport, 'pointerup', function (event) {
        self.onPointerUp(event);
      });
      this.addListener(this.viewport, 'pointercancel', function (event) {
        self.onPointerUp(event);
      });
      this.addListener(this.viewport, 'lostpointercapture', function (event) {
        self.onPointerUp(event);
      });
      this.addListener(this.viewport, 'click', function (event) {
        self.onClick(event);
      }, true);
    }

    this.addListener(document, 'visibilitychange', function () {
      if (document.hidden) self.stopAutoplay();
      else self.startAutoplay();
    });
  };

  SALCMotion.prototype.bindResize = function () {
    var self = this;
    var onResize = debounce(function () {
      window.requestAnimationFrame(function () {
        if (self.isDestroyed) return;

        if (self.options.watchCSS && !self.shouldEnableByCSS()) {
          self.disable();
          return;
        }

        if (!self.isEnabled) {
          self.enable();
          return;
        }

        self.recalculate(true);
      });
    }, 120);

    this.addListener(window, 'resize', onResize);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(onResize);
      this.resizeObserver.observe(this.root);
      if (this.viewport) this.resizeObserver.observe(this.viewport);
    }
  };

  SALCMotion.prototype.setupClones = function () {
    this.removeClones();

    if (!this.options.loop || this.realItems.length < 2) {
      this.cloneCount = 0;
      return;
    }

    this.cloneCount = Math.min(this.realItems.length, this.getFullyVisibleItemCount() + 1);

    var firstClones = this.realItems.slice(0, this.cloneCount).map(this.createClone);
    var lastClones = this.realItems.slice(-this.cloneCount).map(this.createClone);

    for (var lastIndex = lastClones.length - 1; lastIndex >= 0; lastIndex -= 1) {
      this.track.insertBefore(lastClones[lastIndex], this.track.firstChild);
    }

    firstClones.forEach(function (clone) {
      this.track.appendChild(clone);
    }, this);
  };

  SALCMotion.prototype.createClone = function (item) {
    var clone = item.cloneNode(true);
    clone.setAttribute('data-salc-motion-clone', 'true');
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('[id]').forEach(function (node) {
      node.removeAttribute('id');
    });
    clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach(function (node) {
      node.setAttribute('tabindex', '-1');
    });
    return clone;
  };

  SALCMotion.prototype.removeClones = function () {
    this.track.querySelectorAll('[data-salc-motion-clone]').forEach(function (clone) {
      clone.parentNode.removeChild(clone);
    });
  };

  SALCMotion.prototype.getFullyVisibleItemCount = function () {
    if (!this.viewport || !this.realItems.length) return 1;

    var viewportWidth = this.viewport.getBoundingClientRect().width;
    var count = 0;

    this.realItems.forEach(function (item) {
      var itemWidth = item.getBoundingClientRect().width;
      if (itemWidth <= 0) return;
      if (item.offsetLeft + itemWidth <= viewportWidth + 1) count += 1;
    });

    return Math.max(1, count);
  };

  SALCMotion.prototype.hasOverflow = function () {
    if (!this.viewport || !this.track) return false;
    return this.track.scrollWidth - this.viewport.getBoundingClientRect().width > 1;
  };

  SALCMotion.prototype.recalculate = function (rebuildClones) {
    if (this.isDestroyed || !this.isEnabled) return;

    this.realItems = this.getRealItems();

    if (rebuildClones && this.options.loop) {
      var currentIndex = this.index;
      this.setupClones();
      this.items = Array.prototype.slice.call(this.track.children);
      this.index = clamp(currentIndex, 0, this.realItems.length - 1);
      this.positionIndex = this.index + this.cloneCount;
    } else {
      this.items = Array.prototype.slice.call(this.track.children);
    }

    this.applyCenterPadding();

    this.positions = this.items.map(function (item) {
      return item.offsetLeft;
    });

    this.slideStops = this.options.loop ? [] : this.buildSlideStops();

    if (!this.options.loop) {
      this.index = clamp(this.index, 0, Math.max(0, this.slideStops.length - 1));
      this.positionIndex = this.getStopItemIndex(this.index);
    }

    this.buildDots();
    this.moveToPosition(this.positionIndex, false);
    this.updateSelectedItems();
    this.updateDots();
    this.updateButtons();
    this.updateRootState();
  };

  SALCMotion.prototype.previous = function () {
    this.goTo(this.index - 1);
  };

  SALCMotion.prototype.next = function () {
    this.goTo(this.index + 1);
  };

  SALCMotion.prototype.goTo = function (nextIndex) {
    if (this.isDestroyed || !this.isEnabled || this.realItems.length < 1) return;

    if (this.options.loop) {
      this.index = normalizeIndex(nextIndex, this.realItems.length);
      this.positionIndex = nextIndex + this.cloneCount;
    } else {
      this.index = clamp(nextIndex, 0, this.getMaxIndex());
      this.positionIndex = this.getStopItemIndex(this.index);
    }

    this.moveToPosition(this.positionIndex, true);
    this.updateSelectedItems();
    this.updateDots();
    this.updateButtons();
  };

  SALCMotion.prototype.getMaxIndex = function () {
    if (this.options.loop) return this.realItems.length - 1;
    return Math.max(0, this.slideStops.length - 1);
  };

  SALCMotion.prototype.moveToPosition = function (positionIndex, animate, extraOffset) {
    var position = this.options.loop ? this.getTargetPosition(positionIndex) : this.getStopPosition(this.index);
    var offset = typeof extraOffset === 'number' ? extraOffset : 0;

    this.track.classList.toggle('is-animating', Boolean(animate));
    this.track.style.transitionDuration = animate ? this.options.speed + 'ms' : '0ms';
    this.track.style.transform = 'translate3d(' + (-position + offset) + 'px, 0, 0)';

    if (animate && this.options.loop) {
      window.clearTimeout(this.loopTimeout);
      this.loopTimeout = window.setTimeout(this.correctLoopPosition.bind(this), this.options.speed + 30);
    }
  };

  SALCMotion.prototype.getTargetPosition = function (positionIndex) {
    var position = this.positions[positionIndex] || 0;

    if (!this.shouldCenterActiveSlide()) {
      if (!this.options.loop) {
        return Math.min(position, this.getMaxTranslate());
      }

      return position;
    }

    var item = this.items[positionIndex];
    if (!item) return position;

    var viewportWidth = this.viewport.getBoundingClientRect().width;
    var itemWidth = item.getBoundingClientRect().width;

    return position - Math.max(0, (viewportWidth - itemWidth) / 2);
  };

  SALCMotion.prototype.getMaxTranslate = function () {
    if (!this.viewport || !this.track) return 0;
    return Math.max(0, this.track.scrollWidth - this.viewport.getBoundingClientRect().width);
  };

  SALCMotion.prototype.buildSlideStops = function () {
    if (!this.realItems.length) {
      return [{ itemIndex: 0, position: 0 }];
    }

    var stops = [];

    for (var index = 0; index < this.realItems.length; index += 1) {
      var position = this.getTargetPosition(index);
      var lastStop = stops[stops.length - 1];

      if (!lastStop || Math.abs(position - lastStop.position) > 1) {
        stops.push({
          itemIndex: index,
          position: position
        });
      }
    }

    if (!stops.length) {
      stops.push({ itemIndex: 0, position: 0 });
    }

    return stops;
  };

  SALCMotion.prototype.getStopItemIndex = function (stopIndex) {
    if (!this.slideStops.length) return 0;
    var stop = this.slideStops[clamp(stopIndex, 0, this.slideStops.length - 1)];
    return stop ? stop.itemIndex : 0;
  };

  SALCMotion.prototype.getStopPosition = function (stopIndex) {
    if (!this.slideStops.length) return 0;
    var stop = this.slideStops[clamp(stopIndex, 0, this.slideStops.length - 1)];
    return stop ? stop.position : 0;
  };

  SALCMotion.prototype.shouldCenterActiveSlide = function () {
    if (this.options.centerMode) return true;
    return this.options.centerMobile && window.matchMedia('(max-width: 768px)').matches;
  };

  SALCMotion.prototype.applyCenterPadding = function () {
    if (!this.track) return;

    if (!this.shouldCenterActiveSlide() || !this.realItems.length) {
      this.track.style.paddingLeft = '';
      this.track.style.paddingRight = '';
      return;
    }

    var item = this.realItems[0];
    var viewportWidth = this.viewport.getBoundingClientRect().width;
    var itemWidth = item.getBoundingClientRect().width;
    var padding = Math.max(0, (viewportWidth - itemWidth) / 2);

    this.track.style.paddingLeft = padding + 'px';
    this.track.style.paddingRight = padding + 'px';
  };

  SALCMotion.prototype.correctLoopPosition = function () {
    if (!this.options.loop || this.isDestroyed) return;

    if (this.positionIndex >= this.realItems.length + this.cloneCount || this.positionIndex < this.cloneCount) {
      this.positionIndex = this.index + this.cloneCount;
      this.moveToPosition(this.positionIndex, false);
    }
  };

  SALCMotion.prototype.buildDots = function () {
    var self = this;
    var dotCount = this.getDotCount();

    if (!this.dotsContainer || !this.options.pageDots) {
      if (this.dotsContainer) this.dotsContainer.hidden = true;
      this.dots = [];
      return;
    }

    this.dotsContainer.hidden = dotCount <= 1;
    this.dotsContainer.innerHTML = '';

    for (var index = 0; index < dotCount; index += 1) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'salc-motion__dot';
      dot.setAttribute('aria-label', 'Ga naar positie ' + (index + 1));
      dot.addEventListener('click', function (event) {
        self.goTo(Number(event.currentTarget.dataset.salcMotionDotIndex));
      });
      dot.dataset.salcMotionDotIndex = String(index);
      self.dotsContainer.appendChild(dot);
    }

    this.dots = Array.prototype.slice.call(this.dotsContainer.children);
  };

  SALCMotion.prototype.updateSelectedItems = function () {
    if (!this.items.length) return;

    var selectedIndex = this.options.loop ? this.positionIndex : this.getStopItemIndex(this.index);

    this.items.forEach(function (item, index) {
      var isSelected = index === selectedIndex;
      item.classList.toggle('is-selected', isSelected);
      item.classList.toggle('is-active', isSelected);
      item.setAttribute('aria-current', isSelected ? 'true' : 'false');
    });
  };

  SALCMotion.prototype.getDotCount = function () {
    if (!this.realItems.length) return 0;
    if (this.options.loop) return this.realItems.length;
    return this.slideStops.length;
  };

  SALCMotion.prototype.updateDots = function () {
    if (!this.dots.length) {
      if (this.dotsContainer) this.dotsContainer.hidden = true;
      return;
    }

    this.dots.forEach(function (dot, index) {
      var isActive = index === this.index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    }, this);

    if (this.dotsContainer) {
      this.dotsContainer.hidden = this.dots.length <= 1;
    }
  };

  SALCMotion.prototype.updateButtons = function () {
    var hideControls = !this.options.prevNextButtons || this.realItems.length <= 1;

    [this.prevButton, this.nextButton].forEach(function (button) {
      if (!button) return;
      button.hidden = hideControls;
    });

    if (hideControls || this.options.loop) return;

    if (this.prevButton) this.prevButton.disabled = this.index <= 0;
    if (this.nextButton) this.nextButton.disabled = this.index >= this.getMaxIndex();
  };

  SALCMotion.prototype.updateRootState = function () {
    this.root.classList.toggle('is-salc-motion-static', !this.options.loop && this.slideStops.length <= 1);
  };

  SALCMotion.prototype.startAutoplay = function () {
    if (
      this.isDestroyed ||
      !this.isEnabled ||
      !this.options.autoplay ||
      this.realItems.length <= 1 ||
      document.hidden ||
      (window.Shopify && window.Shopify.designMode)
    ) {
      return;
    }

    this.stopAutoplay();
    this.timer = window.setInterval(this.next.bind(this), this.options.autoplayDelay);
  };

  SALCMotion.prototype.stopAutoplay = function () {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  };

  SALCMotion.prototype.onPointerDown = function (event) {
    if (event.button !== undefined && event.button !== 0) return;
    if (event.isPrimary === false) return;
    if (this.realItems.length <= 1) return;
    if (event.target.closest('button, input, select, textarea, label')) return;

    this.pointerId = event.pointerId;
    this.isPointerDown = true;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.dragDelta = 0;
    this.dragAxisLocked = false;
    this.dragMoved = false;
    this.stopAutoplay();
    this.track.classList.remove('is-animating');
    this.viewport.setPointerCapture(event.pointerId);
  };

  SALCMotion.prototype.onPointerMove = function (event) {
    if (!this.isPointerDown || event.pointerId !== this.pointerId) return;

    var deltaX = event.clientX - this.startX;
    var deltaY = event.clientY - this.startY;

    if (!this.dragAxisLocked) {
      if (Math.abs(deltaX) <= 6 && Math.abs(deltaY) <= 6) return;

      this.dragAxisLocked = true;

      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        this.isPointerDown = false;
        this.pointerId = null;
        releasePointerCaptureSafely(this.viewport, event.pointerId);
        this.startAutoplay();
        return;
      }
    }

    this.dragDelta = deltaX;
    event.preventDefault();
    this.dragMoved = true;
    this.moveToPosition(this.positionIndex, false, this.dragDelta);
  };

  SALCMotion.prototype.onPointerUp = function (event) {
    if (!this.isPointerDown || event.pointerId !== this.pointerId) return;

    var threshold = Math.max(35, this.viewport.getBoundingClientRect().width * 0.08);
    this.isPointerDown = false;
    this.pointerId = null;
    this.dragAxisLocked = false;
    releasePointerCaptureSafely(this.viewport, event.pointerId);

    if (this.dragDelta < -threshold) {
      this.suppressClickUntil = Date.now() + 350;
      this.next();
    } else if (this.dragDelta > threshold) {
      this.suppressClickUntil = Date.now() + 350;
      this.previous();
    } else {
      this.moveToPosition(this.positionIndex, true);
    }

    this.startAutoplay();
  };

  SALCMotion.prototype.onClick = function (event) {
    if (!this.dragMoved && Date.now() > this.suppressClickUntil) return;
    if (!event.target.closest('a, button')) return;

    event.preventDefault();
    event.stopPropagation();
    this.dragMoved = false;
  };

  SALCMotion.prototype.disable = function () {
    if (!this.isEnabled) return;

    this.isEnabled = false;
    this.stopAutoplay();
    window.clearTimeout(this.loopTimeout);
    this.removeClones();
    this.track.style.transform = '';
    this.track.style.transitionDuration = '';
    this.track.style.paddingLeft = '';
    this.track.style.paddingRight = '';
    this.track.classList.remove('is-animating');
    this.pointerId = null;
    this.dragMoved = false;
    this.dragAxisLocked = false;
    this.suppressClickUntil = 0;
    this.items.forEach(function (item) {
      item.classList.remove('is-selected', 'is-active');
      item.removeAttribute('aria-current');
    });
    if (this.prevButton) this.prevButton.hidden = true;
    if (this.nextButton) this.nextButton.hidden = true;
    if (this.dotsContainer) this.dotsContainer.hidden = true;
    this.root.classList.remove('is-salc-motion-static');
    this.root.classList.remove('is-salc-motion-ready');
  };

  SALCMotion.prototype.enable = function () {
    if (this.isEnabled) return;

    this.isEnabled = true;
    this.options = this.readOptions();
    this.realItems = this.getRealItems();
    this.bindEvents();
    this.setupClones();
    this.items = Array.prototype.slice.call(this.track.children);
    this.positionIndex = this.options.loop ? this.cloneCount : 0;
    this.root.classList.add('is-salc-motion-ready');
    this.buildDots();
    this.recalculate(false);
    this.startAutoplay();
  };

  SALCMotion.prototype.destroy = function () {
    if (this.isDestroyed) return;

    this.isDestroyed = true;
    this.stopAutoplay();
    window.clearTimeout(this.loopTimeout);
    this.unbinders.forEach(function (unbind) {
      unbind();
    });
    this.unbinders = [];

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.track) {
      this.removeClones();
      this.track.style.transform = '';
      this.track.style.transitionDuration = '';
      this.track.style.paddingLeft = '';
      this.track.style.paddingRight = '';
      this.track.classList.remove('is-animating');
    }

    this.pointerId = null;
    this.dragMoved = false;
    this.dragAxisLocked = false;
    this.suppressClickUntil = 0;
    this.root.classList.remove('is-salc-motion-ready', 'is-salc-motion-static');
    this.root[INSTANCE_KEY] = null;
  };

  function initSALCMotion(scope) {
    var container = scope || document;
    var roots = container.matches && container.matches(ROOT_SELECTOR)
      ? [container]
      : Array.prototype.slice.call(container.querySelectorAll(ROOT_SELECTOR));

    roots.forEach(function (root) {
      if (root[INSTANCE_KEY]) {
        root[INSTANCE_KEY].destroy();
      }

      new SALCMotion(root);
    });
  }

  function destroySALCMotion(scope) {
    var container = scope || document;
    var roots = container.matches && container.matches(ROOT_SELECTOR)
      ? [container]
      : Array.prototype.slice.call(container.querySelectorAll(ROOT_SELECTOR));

    roots.forEach(function (root) {
      if (root[INSTANCE_KEY]) root[INSTANCE_KEY].destroy();
    });
  }

  window.SALCMotion = SALCMotion;
  window.initSALCMotion = initSALCMotion;
  window.destroySALCMotion = destroySALCMotion;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initSALCMotion(document);
    });
  } else {
    initSALCMotion(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initSALCMotion(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroySALCMotion(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var root = event.target.closest(ROOT_SELECTOR);
    if (!root || !root[INSTANCE_KEY]) return;

    var item = event.target.closest('[data-salc-motion-item]');
    var items = root[INSTANCE_KEY].getRealItems();
    var index = items.indexOf(item);

    if (index >= 0) {
      root[INSTANCE_KEY].stopAutoplay();
      root[INSTANCE_KEY].goTo(index);
    }
  });
})();
